// Destructive confirmation dialog that requires typing an exact confirmation
// word (default "DELETE") before the action button enables. Use for
// irreversible admin actions (registration delete, etc.).
import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, TriangleAlert } from 'lucide-react';

interface ConfirmDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  confirmWord?: string;
  actionLabel?: string;
  onConfirm: () => void | Promise<void>;
  loading?: boolean;
}

export default function ConfirmDeleteDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmWord = 'DELETE',
  actionLabel = 'Delete',
  onConfirm,
  loading = false,
}: ConfirmDeleteDialogProps) {
  const [typed, setTyped] = useState('');

  // Reset the confirmation input whenever the dialog closes/opens.
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const confirmed = typed === confirmWord;

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!loading) onOpenChange(o); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <TriangleAlert className="h-5 w-5 text-red-600" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div>{description}</div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-delete-input" className="text-foreground">
                  Type <span className="font-mono font-semibold text-red-600">{confirmWord}</span> to confirm
                </Label>
                <Input
                  id="confirm-delete-input"
                  autoComplete="off"
                  autoFocus
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={confirmWord}
                />
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!confirmed || loading}
            onClick={() => onConfirm()}
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {actionLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
